import { FC, useCallback, useState, useEffect, useMemo, createContext } from 'react'
import { clearAllBodyScrollLocks, disableBodyScroll } from 'helpers/scrollHelpers'
import { generate } from 'randomstring'

export type PreventScrollContextType = {
  onScrollPreventionChange: () => void
}

export const PreventScrollContext = createContext<PreventScrollContextType>({
  onScrollPreventionChange: () => { /* do nothing */ },
})

/**
 * There are 2 types of nodes dependant on scroll:
 * * Which want others like body to be locked (scroll prevented). Such components as `FloatingPane`, all the overlays, etc.
 * * Which just don't want to be locked in any case. Sucha components as `DropdownItems`, 'Insights`, etc.
 *
 * Therefore you can see 2 different values of `data-scroll` attributes: `prevent-others` and `allow`.
 *
 * The overall logic is the following: this context provider throws it's handler to downstream.
 * The handler may be triggered by anyone in case of a `data-scroll` nodes picture changed. Usually `PreventScroll` component does this.
 * The overall process of evaluating of the picture is starting when the handler is triggered.
 *
 * !!! This is very important to explicitly walt thru every node and `disableScroll` to it because iOS implementation requires this.
 *
 * The component is looking for elements which want to `prevent-others`. And it locks everything except of `prevent-others` and `allowed` nodes if found.
 * Otherwise — clear the locks.
 */

export const PreventScrollContextProvider: FC = (props) => {
  const { children } = props
  const [key, setKey] = useState(generate())

  const handleScrollPreventionChange = useCallback(() => {
    const nextKey = generate()
    setKey(nextKey)
  }, [])

  useEffect(() => {
    const doc = document.documentElement
    const blockingNodes = Array.from(doc.querySelectorAll<HTMLElement>('[data-scroll=prevent-others]'))
    if (!blockingNodes.length) {
      clearAllBodyScrollLocks()
      return
    }

    const allowedNodes = Array.from(doc.querySelectorAll<HTMLElement>('[data-scroll=allow]'))

    const allNodes = [...blockingNodes, ...allowedNodes]

    /* tslint:disable-next-line:no-unnecessary-callback-wrapper */
    allNodes.forEach((node) => disableBodyScroll(node, { reserveScrollBarGap: true }))

    return () => {
      clearAllBodyScrollLocks()
    }
  }, [key])

  const contextValue = useMemo<PreventScrollContextType>(() => ({
    onScrollPreventionChange: handleScrollPreventionChange,
  }), [handleScrollPreventionChange])

  return (
    <PreventScrollContext.Provider value={ contextValue }>
      { children }
    </PreventScrollContext.Provider>
  )
}

